/**
 * Boletim de Medição (BDM) — visualização consolidada para o cliente.
 * Agrupa por trecho, mostra totais por categoria, detalhamento com fotos.
 * Suporta impressão (window.print → PDF) e export Excel.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { BdmBrand, bdmVars } from "@/components/comercial/BdmBrand";
import { PageHeader } from "@/components/pcp";
import { ArrowLeft, Printer, FileArrowDown, MapPinLine, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { gerarRelatorioFotografico } from "@/lib/relatorioFotografico";

function fmtN(n, casas = 2) {
  const v = parseFloat(n) || 0;
  return v.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

function TabelaPlacasTrecho({ linhas }) {
  const placas = linhas.filter((l) => l.placa);
  if (placas.length === 0) return null;
  const totalUn = placas.reduce((s, l) => s + (l.placa.qtd_placas || 1), 0);
  const totalM2 = placas.reduce((s, l) => s + (l.placa.area_total_m2 || 0), 0);
  return (
    <>
      <div className="bdm-meta" style={{ marginTop: 10 }}>Placas instaladas neste trecho</div>
      <table className="bdm-table bdm-table-placas" data-testid="bdm-tabela-placas-trecho">
        <thead>
          <tr>
            <th>Data</th>
            <th>Km</th>
            <th>Lado</th>
            <th>Placa</th>
            <th>Categoria</th>
            <th>Dimensões</th>
            <th>Substrato</th>
            <th>Película</th>
            <th>Suporte</th>
            <th style={{ textAlign: "right" }}>Qtd</th>
            <th style={{ textAlign: "right" }}>Área (m²)</th>
          </tr>
        </thead>
        <tbody>
          {placas.map((l) => {
            const p = l.placa;
            return (
              <tr key={l.apontamento_id}>
                <td>{fmtDate(l.data)}</td>
                <td className="mono">{p.km || "—"}</td>
                <td>{p.lado_via || "—"}</td>
                <td><strong>{p.codigo}</strong>{p.nome ? ` · ${p.nome}` : ""}</td>
                <td>{p.categoria || "—"}</td>
                <td className="mono">{p.tamanho_padrao ? `${p.tamanho_padrao} · ` : ""}{p.dimensoes || "—"}</td>
                <td>{p.substrato || "—"}</td>
                <td>{p.pelicula || "—"}</td>
                <td>{p.tipo_suporte || "—"}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmtN(p.qtd_placas, 0)}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmtN(p.area_total_m2, 3)}</td>
              </tr>
            );
          })}
          <tr className="bdm-total-row">
            <td colSpan={9}><strong>TOTAL DO TRECHO</strong></td>
            <td style={{ textAlign: "right" }} className="mono"><strong>{fmtN(totalUn, 0)}</strong></td>
            <td style={{ textAlign: "right" }} className="mono"><strong>{fmtN(totalM2, 3)}</strong></td>
          </tr>
        </tbody>
      </table>
    </>
  );
}

export default function BoletimMedicao() {
  const { id } = useParams();
  const [bdm, setBdm] = useState(null);
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/ordens/${id}/bdm`)
      .then((r) => { setBdm(r.data); const eid = r.data.os?.empresa_executora_id; if (eid) api.get("/empresas").then((e) => setEmp(e.data.find((x) => x.id === eid) || null)).catch(() => null); })
      .catch(() => toast.error("Erro ao carregar BDM"))
      .finally(() => setLoading(false));
  }, [id]);

  const [pdfProg, setPdfProg] = useState(null);
  const baixarPdf = async () => {
    setPdfProg({ status: "gerando" });
    try { await gerarRelatorioFotografico(`/ordens/${id}/bdm/pdf/jobs`, `Boletim_Medicao_OS_${bdm?.os?.numero || id}.pdf`, setPdfProg); toast.success("Boletim em PDF gerado"); }
    catch (e) { toast.error(e.message || "Erro ao gerar o PDF"); }
    finally { setPdfProg(null); }
  };
  const baixarExcel = async () => {
    try {
      const res = await api.get(`/ordens/${id}/bdm/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `BDM_${bdm?.os?.numero || id}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao baixar Excel");
    }
  };

  if (loading) return <div style={{ padding: 32 }}>Carregando BDM…</div>;
  if (!bdm) return <div style={{ padding: 32 }}>Sem dados</div>;

  const totalPlacas = bdm.trechos.reduce((acc, g) => acc + g.linhas.filter((l) => l.tipo_key.startsWith("placa:")).length, 0);
  const totalPintura = bdm.trechos.reduce((acc, g) => acc + g.linhas.filter((l) => l.tipo_key.startsWith("pintura:")).length, 0);
  const totalGeral = bdm.trechos.reduce((acc, g) => acc + g.linhas.filter((l) => l.tipo_key.startsWith("geral:")).length, 0);
  const temResumoPlacas = bdm.resumo_placas && bdm.resumo_placas.length > 0;
  const secTrecho = temResumoPlacas ? 3 : 2;

  return (
    <>
      <div className="bdm-toolbar no-print">
        <PageHeader
          testid="page-bdm"
          title={`Boletim de Medição · OS ${bdm.os.numero}`}
          subtitle={`${bdm.os.contratante || ""} · ${bdm.os.rodovia || ""} ${bdm.os.trecho || ""}`.trim()}
          actions={
            <>
              <Link to={`/ordens/${id}`} className="btn btn-outline">
                <ArrowLeft size={16} weight="bold" /> Voltar à OS
              </Link>
              <button className="btn btn-outline" onClick={baixarExcel} data-testid="btn-bdm-excel">
                <FileArrowDown size={16} weight="bold" /> Excel
              </button>
              <button className="btn btn-primary" disabled={!!pdfProg} onClick={baixarPdf} data-testid="btn-bdm-pdf">
                <Printer size={16} weight="bold" /> {pdfProg ? (pdfProg.status === "montando" ? `Montando PDF… ${pdfProg.montadas || 0}/${pdfProg.total || 0}` : `Preparando fotos… ${pdfProg.feitas || 0}/${pdfProg.total || 0}`) : "Baixar PDF"}
              </button>
              <button className="btn btn-ghost" onClick={() => window.print()} title="Impressão rápida pelo navegador" data-testid="btn-bdm-imprimir">
                Imprimir
              </button>
            </>
          }
        />
      </div>

      <div className="bdm-doc" style={bdmVars(emp)}>
        {/* Cabeçalho oficial do documento */}
        <div className="bdm-header">
          <BdmBrand emp={emp} />
          <div className="bdm-title">
            <h1>BOLETIM DE MEDIÇÃO</h1>
            <div className="bdm-os-num">OS {bdm.os.numero}</div>
          </div>
        </div>

        <table className="bdm-info-table">
          <tbody>
            <tr>
              <th>Contratante</th><td>{bdm.os.contratante || "—"}</td>
              <th>Rodovia</th><td>{bdm.os.rodovia || "—"} {bdm.os.trecho || ""}</td>
            </tr>
            <tr>
              <th>Município</th><td>{bdm.os.municipio || "—"}</td>
              <th>Início produção</th><td>{fmtDate(bdm.os.data_inicio_producao)}</td>
            </tr>
            <tr>
              <th>Prazo</th><td>{fmtDate(bdm.os.prazo_conclusao)}</td>
              <th>Gerado em</th><td>{fmtDate(bdm.gerado_em)}</td>
            </tr>
          </tbody>
        </table>

        {/* Resumo geral */}
        <h2 className="bdm-h2">1. Resumo Geral por Categoria</h2>
        {bdm.totais.length === 0 ? (
          <p className="bdm-empty">Nenhuma medição registrada ainda.</p>
        ) : (
          <table className="bdm-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th style={{ textAlign: "center" }}>Unidade</th>
                <th style={{ textAlign: "right" }}>Quantidade total</th>
                <th style={{ textAlign: "right" }}>Área total (m²)</th>
              </tr>
            </thead>
            <tbody>
              {bdm.totais.map((t, i) => (
                <tr key={i}>
                  <td>{t.categoria}</td>
                  <td style={{ textAlign: "center" }}>{t.unidade}</td>
                  <td style={{ textAlign: "right" }} className="mono">{fmtN(t.qtd_total, 2)}</td>
                  <td style={{ textAlign: "right" }} className="mono">{fmtN(t.area_total_m2, 3)}</td>
                </tr>
              ))}
              <tr className="bdm-total-row">
                <td colSpan={2}><strong>TOTAL GERAL</strong></td>
                <td style={{ textAlign: "right" }} className="mono">
                  <strong>{fmtN(bdm.totais.reduce((s, t) => s + t.qtd_total, 0), 2)}</strong>
                </td>
                <td style={{ textAlign: "right" }} className="mono">
                  <strong>{fmtN(bdm.totais.reduce((s, t) => s + t.area_total_m2, 0), 3)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        )}
        <div className="bdm-meta">
          {totalPlacas} placas · {totalPintura} apontamentos de pintura · {totalGeral} demais serviços
        </div>

        {/* Resumo de placas por código CONTRAN */}
        {bdm.resumo_placas && bdm.resumo_placas.length > 0 && (
          <>
            <h2 className="bdm-h2">2. Resumo de Placas por Código CONTRAN</h2>
            <table className="bdm-table" data-testid="bdm-resumo-placas">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Placa</th>
                  <th>Categoria</th>
                  <th style={{ textAlign: "right" }}>Qtd apts</th>
                  <th style={{ textAlign: "right" }}>Placas (un)</th>
                  <th style={{ textAlign: "right" }}>Área total (m²)</th>
                </tr>
              </thead>
              <tbody>
                {bdm.resumo_placas.map((r) => (
                  <tr key={r.codigo}>
                    <td><strong>{r.codigo}</strong></td>
                    <td>{r.nome || "—"}</td>
                    <td>{r.categoria || "—"}</td>
                    <td style={{ textAlign: "right" }} className="mono">{r.qtd_apontamentos}</td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtN(r.qtd_placas, 0)}</td>
                    <td style={{ textAlign: "right" }} className="mono">{fmtN(r.area_total_m2, 3)}</td>
                  </tr>
                ))}
                <tr className="bdm-total-row">
                  <td colSpan={3}><strong>TOTAL PLACAS</strong></td>
                  <td style={{ textAlign: "right" }} className="mono"><strong>{bdm.resumo_placas.reduce((s, r) => s + r.qtd_apontamentos, 0)}</strong></td>
                  <td style={{ textAlign: "right" }} className="mono"><strong>{fmtN(bdm.resumo_placas.reduce((s, r) => s + r.qtd_placas, 0), 0)}</strong></td>
                  <td style={{ textAlign: "right" }} className="mono"><strong>{fmtN(bdm.resumo_placas.reduce((s, r) => s + r.area_total_m2, 0), 3)}</strong></td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {/* Por trecho */}
        <h2 className="bdm-h2">{secTrecho}. Detalhamento por Trecho</h2>
        {bdm.trechos.map((g, idx) => {
          const nome = g.trecho
            ? `${g.trecho.nome} · km ${g.trecho.km_inicial} → ${g.trecho.km_final}`
            : "Sem trecho cadastrado";
          const km = g.trecho
            ? `${((g.trecho.extensao_m || 0) / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} km`
            : "";
          return (
            <div key={idx} className="bdm-trecho">
              <div className="bdm-trecho-head">
                <MapPinLine size={18} weight="bold" />
                <div>
                  <div className="bdm-trecho-name">{nome}</div>
                  {km && <div className="bdm-trecho-ext">Extensão: {km}</div>}
                </div>
              </div>

              {g.resumo.length > 0 && (
                <table className="bdm-table">
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th>Serviço</th>
                      <th style={{ textAlign: "center" }}>Un</th>
                      <th style={{ textAlign: "right" }}>Qtd apts</th>
                      <th style={{ textAlign: "right" }}>Quantidade</th>
                      <th style={{ textAlign: "right" }}>Área (m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.resumo.map((r, i) => (
                      <tr key={i}>
                        <td>{r.categoria}</td>
                        <td>{r.tipo}</td>
                        <td style={{ textAlign: "center" }}>{r.unidade}</td>
                        <td style={{ textAlign: "right" }} className="mono">{r.qtd_apontamentos}</td>
                        <td style={{ textAlign: "right" }} className="mono">{fmtN(r.qtd_total, 2)}</td>
                        <td style={{ textAlign: "right" }} className="mono">{fmtN(r.area_total_m2, 3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <TabelaPlacasTrecho linhas={g.linhas} />
            </div>
          );
        })}

        {/* Detalhamento com fotos */}
        <h2 className="bdm-h2">{secTrecho + 1}. Comprovação em Campo</h2>
        <div className="bdm-meta">Fotos com data, GPS e código estampados no momento da execução.</div>

        {bdm.trechos.map((g, idx) => {
          const nome = g.trecho
            ? `${g.trecho.nome} · km ${g.trecho.km_inicial} → ${g.trecho.km_final}`
            : "Sem trecho cadastrado";
          if (g.linhas.length === 0) return null;
          return (
            <div key={idx} className="bdm-fotos-trecho">
              <h3 className="bdm-h3"><MapPinLine size={14} weight="bold" /> {nome}</h3>
              {g.linhas.map((l) => (
                <div key={l.apontamento_id} className="bdm-linha">
                  <div className="bdm-linha-head">
                    <div>
                      <span className="bdm-chip">{l.categoria}</span>
                      <strong style={{ marginLeft: 6 }}>{l.tipo_label}</strong>
                    </div>
                    <div className="bdm-linha-data">{fmtDate(l.data)}</div>
                  </div>
                  <div className="bdm-linha-info">
                    {l.km_inicial && <span>km {l.km_inicial}{l.km_final && ` → ${l.km_final}`}</span>}
                    {l.detalhes && <span>{l.detalhes}</span>}
                    {l.observacao && <span>Obs: {l.observacao}</span>}
                  </div>
                  {l.fotos && l.fotos.length > 0 && (
                    <div className="bdm-fotos-grid">
                      {l.fotos.map((f, i) => (
                        <a key={i} href={f} target="_blank" rel="noreferrer"><img src={f.startsWith("/api/") ? `${f}?w=640` : f} alt="foto de campo" /></a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <div className="bdm-assinaturas">
          <div className="bdm-assinatura">
            <div className="bdm-linha-ass" />
            <div>Contratada</div>
          </div>
          <div className="bdm-assinatura">
            <div className="bdm-linha-ass" />
            <div>Fiscalização</div>
          </div>
        </div>

        <div className="bdm-footer">
          Documento gerado eletronicamente pelo Sistema PCP OSTECNO VIAS · {fmtDate(bdm.gerado_em)}
        </div>
      </div>
    </>
  );
}
